import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { registerPartnerCeleb, unregisterPartnerCeleb } from '../api/authAdmin';
import {
  REGISTER_PARTNER_CELEB_ACTION,
  UNREGISTER_PARTNER_CELEB_ACTION,
  getPartnerCelebAction,
  mapPartnerCelebError,
} from './partnerCelebActions';
import { getUserId, getUserUsername } from './userBanActions';
import { t } from './i18n';

/**
 * Shared partner celeb register/unregister mutations for User/Celeb pages.
 */
export function usePartnerCelebActions({ token, apiEnvironment, onUnauthorized }) {
  const queryClient = useQueryClient();
  const [pendingUserId, setPendingUserId] = useState(null);
  const [dialog, setDialog] = useState(null);

  const invalidateAfterPartnerCelebChange = (userId) => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['celebs'] });
    queryClient.invalidateQueries({ queryKey: ['usersSummary', apiEnvironment] });
    if (userId != null) {
      queryClient.invalidateQueries({ queryKey: ['userById', apiEnvironment, userId] });
      queryClient.invalidateQueries({ queryKey: ['user-detail', apiEnvironment] });
    }
  };

  const registerMutation = useMutation({
    mutationFn: (userId) => registerPartnerCeleb(userId, token),
    onSettled: () => setPendingUserId(null),
  });

  const unregisterMutation = useMutation({
    mutationFn: (userId) => unregisterPartnerCeleb(userId, token),
    onSettled: () => setPendingUserId(null),
  });

  const isPending = registerMutation.isPending || unregisterMutation.isPending;

  const closeDialog = () => {
    if (isPending) return;
    setDialog(null);
  };

  const runPartnerCelebAction = (user) => {
    const action = getPartnerCelebAction(user);
    const userId = getUserId(user);
    if (!action || userId == null || !token) return;

    const username = getUserUsername(user) || String(userId);
    const messageKey =
      action === REGISTER_PARTNER_CELEB_ACTION
        ? 'user.partnerCeleb.confirmRegister'
        : 'user.partnerCeleb.confirmUnregister';
    setDialog({
      kind: 'confirm',
      user,
      action,
      message: t(messageKey, { id: userId, username }),
    });
  };

  const confirmDialog = async () => {
    if (!dialog || dialog.kind !== 'confirm') return;

    const { user, action } = dialog;
    const userId = getUserId(user);
    if (userId == null || !token) return;

    setPendingUserId(userId);
    try {
      if (action === REGISTER_PARTNER_CELEB_ACTION) {
        await registerMutation.mutateAsync(userId);
      } else if (action === UNREGISTER_PARTNER_CELEB_ACTION) {
        await unregisterMutation.mutateAsync(userId);
      }
      invalidateAfterPartnerCelebChange(userId);
      const successKey =
        action === REGISTER_PARTNER_CELEB_ACTION
          ? 'user.partnerCeleb.successRegister'
          : 'user.partnerCeleb.successUnregister';
      setDialog({
        kind: 'alert',
        variant: 'success',
        message: t(successKey, { id: userId }),
      });
    } catch (err) {
      if (err?.status === 401) {
        setDialog(null);
        onUnauthorized?.();
        return;
      }
      setDialog({
        kind: 'alert',
        variant: 'error',
        message: mapPartnerCelebError(err),
      });
    }
  };

  return {
    runPartnerCelebAction,
    isPending,
    pendingUserId,
    getPartnerCelebAction,
    dialog,
    confirmDialog,
    closeDialog,
  };
}
